import { createTweekClient as createUnAuthenticatedTweekClient, Context, ITweekClient } from 'tweek-client';
import zipObj from 'ramda/src/zipObj';
import { logger, monitorClient, userAgentParser } from '@soluto-home-web/platform-utils';
import { TweekRepository, FlatKeys } from 'tweek-local-cache';
import merge from 'lodash.merge';
import TweekClientWithFallback from './TweekClientWithFallback';
import exponentRefreshErrorPolicy from './exponentRefreshErrorPolicy';

let TWEEK_URL = 'https://tweek.mysoluto.com';
let TWEEK_FALLBACK_URL = 'https://tweek-west.mysoluto.com';

let _client: ITweekClient;
let _tweekRepository: TweekRepository;

export type TweekParams = {
  store?: any;
  origin: string;
  expertDetails: { email: string; roles: Array<string>; queues: Array<string> };
  appVersion: string;
};

const createTweekClientWithMonitor = (tweekUrl: string) => {
  const client = createUnAuthenticatedTweekClient({
    baseServiceUrl: tweekUrl,
    useLegacyEndpoint: true,
  });
  if (userAgentParser.isIEBrowser()) {
    client.config.maxChunkSize = 10;
  }

  const tweekFetch = client.config.fetch;
  const monitoredFetch = async (request: RequestInfo, init?: RequestInit): Promise<Response> => {
    const measuringFunction = monitorClient.startMeasureTime();
    const response = await tweekFetch(request, init);
    try {
      measuringFunction.monitor(`get-tweek-keys.${tweekUrl.replace('https://', '').split('.')[0]}`);
    } catch {}
    return response;
  };
  client.config.fetch = monitoredFetch;
  return client;
};

export function initTweekRepo(context: Context, tweekDefaults: FlatKeys) {
  const refreshErrorPolicy = exponentRefreshErrorPolicy(300, 5);
  const fallbackClients = [TWEEK_URL, TWEEK_FALLBACK_URL].map((url) => createTweekClientWithMonitor(url));
  _client = new TweekClientWithFallback(fallbackClients);
  _tweekRepository = new TweekRepository({ client: _client, refreshErrorPolicy, context });

  _tweekRepository.addKeys(tweekDefaults);
  prepareScanKeys(tweekDefaults, _tweekRepository);

  _tweekRepository.refresh();
  (window as any).tweekRepo = _tweekRepository;
  return _tweekRepository;
}

const prepareScanKeys = (tweekDefaults, repository) => {
  Object.keys(tweekDefaults)
    .filter((k) => k.endsWith('_'))
    .forEach((k) => repository.prepare(k));
};

export const getTweekRepository = () => _tweekRepository;

export const getFromTweek = async (key: any) => {
  if (!_tweekRepository) throw 'tweek need to be initialized';
  try {
    const result = await _tweekRepository.get(key);
    return result.value;
  } catch (e) {
    logger.error(`failed get value from tweek`, e, key);
    throw 'failed get value from tweek';
  }
};

export const getMultipleFromTweek = async (paths: Array<string>) => {
  const values = await Promise.all(paths.map((path) => getFromTweek(path)));
  return zipObj(paths, values);
};

export const appendAndRefreshTweekContext = (context) => {
  _tweekRepository.updateContext((c) => merge(c, ...context));
};



// WEBPACK FOOTER //
// ./packages/platform-core/tweek/tweekClient.ts